import React, { useEffect, useState } from "react";
import { TextField, Button, Switch, FormControlLabel, FormControl, Grid, Box, InputAdornment, Paper, Card, CardContent, Typography, IconButton, Tooltip } from "@mui/material";
import { Pie } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    ArcElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    ChartOptions,

} from "chart.js";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../Context/Contexts";
import { ReportFilterViewModel, ListQuery, ReportSalesVariantViewModel, ProgressResultViewModel } from "../../Types/ExportTypes";
import CenteredCard from "../../components/Card/CenteredCardLayout";
import ReportHeader from "../../components/Report/ReportHeader";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import { useTranslation } from "react-i18next";
import DownloadIcon from '@mui/icons-material/GridOn';
import LoadingSection from "../../components/LoadingSection";
import ResponsivePagination from "../../components/ResponsivePagination";


ChartJS.register(CategoryScale, LinearScale, ArcElement, Title, ChartTooltip, Legend);

const SalesItemReportPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { showLoading, hideLoading } = useLoading();
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();

    const { goBack } = useNavigation();
    const [filter, setFilter] = useState<ReportFilterViewModel>({});
    const [searchFilter, setSearchFilter] = useState<string>("");
    const [query, setQuery] = useState<ListQuery>({
        page: 0,
        pageSize: 10,
        filter: "",
        filters: [],
    });
    const [linesLoading, setLinesLoading] = useState(false);
    const [items, setItems] = useState<ReportSalesVariantViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [TotalGraphSum, setTotalGraphSum] = useState(0);
/*    const [totalGraphQty, setTotalGraphQty] = useState(0);*/
    const [initialized, setInitialized] = useState(false);

    const { t } = useTranslation();

    const getGraphData = (itemCollection?: ReportSalesVariantViewModel[]) => {
        const topItems = [...itemCollection ?? items].sort((a, b) => (b.amountIncludingVAT ?? 0) - (a.amountIncludingVAT ?? 0)).slice(0, 100);

        return {
            labels: topItems.map(item => {
                const variantParts = [item.color, item.size, item.length].filter(Boolean);
                const variantInfo = variantParts.length ? variantParts.join("/") : "";
                return `${item.itemNo} - ${variantInfo ? variantInfo + " - " : ""}${item.description}`;
            }),
            datasets: [
                {
                    label: t("common.salesDistribution") ,
                    data: topItems.map(item => item.amountIncludingVAT),
                    backgroundColor: topItems.map(() => "#" + Math.floor(Math.random() * 16777215).toString(16)), // Random colors
                    borderWidth: 1,
                },
            ],
        };
    };
    const [graphData, setGraphData] = useState(getGraphData);


    const options: ChartOptions<"pie"> = {
        responsive: true,
        plugins: {
            legend: { position: "bottom" },
            title: { display: true, text: t("common.salesDistribution") },

        },
    };


    const fetchDocuments = async (initializedOverLoad? : boolean |null) => {
        showLoading();
        setLinesLoading(true);
        try {
            const response = await authService.post<{ items: ReportSalesVariantViewModel[], count: number, topResult: ReportSalesVariantViewModel[] }>(
                `/reportApi/GetVariantReport`,
                query
            );
            setItems(prev => {

                if (initializedOverLoad === false || !initialized) {
                    // proceed with fetch
                
                    console.log("setting graphData");
                    var loadGraphData = getGraphData(response.topResult);
                    setGraphData(loadGraphData);
                    //const updatedItems = response.items.map((item) => ({
                    //    ...item,
                    //    addedToGraph: loadGraphData.labels.some((topItem) => topItem === item.itemNo + " - " + item.description),
                    //}))

                    setInitialized(true);
                   // setTotalCount(response.count);
                   // return response.items; 
                }

                //const updatedItems = response.items.map((item) => ({
                //    ...item,
                //    addedToGraph: graphData.labels.some((topItem) => topItem === item.itemNo + " - " + item.description),
                //}))


                setTotalCount(response.count);
                return response.items; // ✅ Return the modified array
            });


        } catch (error) {
            console.error("Failed to fetch documents:", error);
        } finally {
            hideLoading();
            setLinesLoading(false); 
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, [query]);

    useEffect(() => {
        const newTotal = graphData.datasets[0].data.reduce((sum, value) => (sum ?? 0) + (value ?? 0), 0) ?? 0;
        const elementCount = graphData.datasets[0].data.length ?? 0;
        setTotalGraphSum(newTotal);
        //setTotalGraphQty(elementCount);
    }, [graphData]);


    const generateReport = async (filter: ReportFilterViewModel) => {
        setFilter(filter);
        showLoading();
        try {
            const response = await authService.post<{ progressId: number }>(
                `/reportApi/GenerateVariantReport`,
                filter
            );
            await pollStatus(response.progressId);
        } catch (error) {
            console.error("Failed to fetch documents:", error);
        } finally {
            hideLoading();
        }
    };


    const pollStatus = async (progressId: number) => {
        const interval = 5000;
        let attempts = 100;

        while (attempts > 0) {
            try {
                const response = await authService.get<ProgressResultViewModel>(`/progressApi/GetProgress?progressId=${progressId}`);

                if (!response) throw new Error("Failed to fetch status");
                if (response.done) {
                    setInitialized((prev) => {
                        fetchDocuments(false);
                        return false;
                    })
                    break;
                }

            } catch (error) {
                console.error("Error fetching status:", error);
                showSnackbar(t("dialog.fetchStatusError"), "error");
                return;
            }

            attempts--;
            await new Promise((resolve) => setTimeout(resolve, interval));
        }
    };
    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setQuery({ ...query, filter: searchFilter, page: 0 });
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setQuery({ ...query, pageSize: newPageSize, page: 0 });
    };



    //const toggleItemForGraph = (item: ReportSalesItemViewModel) => {
    //    setGraphData((prevData) => {
    //        const itemIndex = prevData.labels.findIndex(
    //            (label) => label === item.itemNo + " - " + item.description
    //        );
    //        console.log("item", [item, prevData.labels, itemIndex])

    //        if (itemIndex !== -1) {
    //            const newLabels = prevData.labels.filter((_, index) => index !== itemIndex);
    //            const newData = prevData.datasets[0].data.filter((_, index) => index !== itemIndex);
    //            const newColors = prevData.datasets[0].backgroundColor.filter((_, index) => index !== itemIndex);

    //            setItems((prevItems) => {
    //                const updatedItems = prevItems.map((i) =>
    //                    i.id === item.id ? { ...i, addedToGraph: !i.addedToGraph } : i
    //                );
    //                return [...updatedItems]; // Return a new array reference
    //            });


    //            if (newLabels.length === 0) {
    //                showSnackbar(t("dialog.graphEmpty"), "info");
    //                return {
    //                    labels: [],
    //                    datasets: [{ label: t("common.salesDistribution"), data: [], backgroundColor: [], borderWidth: 1 }],
    //                };
    //            }
    //            showSnackbar(`${t("dialog.graphRemoveItem")} ${item.itemNo} ${item.description}`, "success");


    //            return {
    //                labels: newLabels,
    //                datasets: [{ ...prevData.datasets[0], data: newData, backgroundColor: newColors }],
    //            };
    //        }
    //        setItems((prevItems) => {
    //            const updatedItems = prevItems.map((i) =>
    //                i.itemNo === item.itemNo ? { ...i, addedToGraph: !i.addedToGraph } : i
    //            );
    //            return [...updatedItems]; // Return a new array reference
    //        });

    //        showSnackbar(`${t("dialog.graphAddItem")} ${item.itemNo} ${item.description}`, "success");
    //        return {
    //            labels: [...prevData.labels, item.description ?? ""],
    //            datasets: [
    //                {
    //                    ...prevData.datasets[0],
    //                    data: [...prevData.datasets[0].data, item.amountIncludingVAT ?? 0],
    //                    backgroundColor: [
    //                        ...prevData.datasets[0].backgroundColor,
    //                        "#" + Math.floor(Math.random() * 16777215).toString(16), // Random color
    //                    ],
    //                },
    //            ],
    //        };
    //    });
    //};

    const handleExcel = async () => {
        try {
            const response = await authService.post<Blob>(
                "/reportApi/GetVariantReportExcel",
                {},
                "blob"
            );


            const url = window.URL.createObjectURL(response);
            const a = document.createElement("a");
            a.href = url;
            a.download = "VariantReport.xlsx";
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download Excel report", error);
        }
    };

    return (
        <>
            <CenteredCard title={t("common.variantReport")} isLoading={false}>
                <ReportHeader filter={filter} onGenerate={generateReport} />
                {items.length > 0 && (
                    <>
                        <Box sx={{ mt: 4, display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
                            <Pie data={graphData} options={options} />
                        </Box>
                        
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Typography variant="h6" sx={{ fontWeight: "bold", textAlign: "center" }}>
                                {t("common.totalSum")}: {TotalGraphSum?.toLocaleString()} LCY
                            </Typography>


                        </Box>

                    </>
                )}
                <Paper sx={{ padding: 2 }}>
                    {/* Search Field */}
                    <Box
                        component="form"
                        onSubmit={handleSearchSubmit}
                        sx={{
                            display: "flex",
                            gap: 2,
                            marginBottom: 2,
                        }}
                    >
                        <TextField
                            label={t("common.search")}
                            variant="outlined"
                            fullWidth
                            onChange={(event) => setSearchFilter(event.target.value)}
                            value={searchFilter}

                        />
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"

                        >
                            {t("common.search")}
                        </Button>
                    </Box>
                    {/* Card Layout for Items */}
                    <LoadingSection loading={linesLoading}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Tooltip title={t("help.downloadExcel")}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    fullWidth
                                    onClick={handleExcel}
                                    startIcon={<DownloadIcon />}
                                >
                                    Excel
                                </Button>
                            </Tooltip>
                        </Grid>
                        {items.map((item) => (
                            <Grid item xs={12} key={item.itemNo}>
                                <Grid container spacing={1} sx={{
                                    p: 1.5,
                                    border: '1px solid #ccc',
                                    borderRadius: 1,
                                    cursor: "pointer",
                                    transition: "all 0.2s ease-in-out",
                                    ':hover': {
                                        backgroundColor: '#f9f9f9'
                                    }
                                }}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" fontWeight="bold">{item.itemNo}</Typography>
                                    </Grid>
                                    {/*<Grid item xs={4}>*/}
                                    {/*    <IconButton onClick={() => toggleItemForGraph(item)} sx={{ ml: 2 }}>*/}
                                    {/*        {item.addedToGraph ? (*/}
                                    {/*            <Tooltip title={t("common.removeFromGraph")}>*/}
                                    {/*                <CancelIcon sx={{ color: "red" }} />*/}
                                    {/*            </Tooltip>*/}
                                    {/*        ) : (*/}
                                    {/*                <Tooltip title={t("common.addToGraph")}>*/}
                                    {/*                <AddCircleOutlineIcon sx={{ color: "green" }} />*/}
                                    {/*            </Tooltip>*/}
                                    {/*        )}*/}
                                    {/*    </IconButton>*/}
                                    {/*</Grid>*/}

                                    <Grid item xs={6} md={4}>
                                        <Typography variant="body2"> {t("common.description")}:</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                            {item.description}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={6} md={4}>
                                        <Typography variant="body2">{t("common.variant")}:</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                            {[
                                                (item.colorCode && item.color) ? `${t("common.colour")}: ${item.colorCode} - ${item.color}` : item.color || item.colorCode,
                                                item.size && `${t("common.size") }: ${item.size}`,
                                                item.length && `${t("common.length") }: ${item.length}`
                                            ].filter(Boolean).join(" / ")}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={6} md={4}>
                                        <Typography variant="body2"> {t("common.itemGroup")}:</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{item.itemGroup}</Typography>
                                    </Grid>

                                    <Grid item xs={6} md={4}>
                                        <Typography variant="body2"> {t("common.itemCategory")}:</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{item.itemCategory}</Typography>
                                    </Grid>

                                    <Grid item xs={6} md={4}>
                                        <Typography variant="body2"> {t("common.productGroup")}:</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{item.productGroup}</Typography>
                                    </Grid>

                                    <Grid item xs={6} md={4}>
                                        <Typography variant="body2"> {t("common.program")}:</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{item.program}</Typography>
                                    </Grid>

                                    <Grid item xs={6} md={12}>
                                        <Typography variant="body2"> {t("common.collection")}:</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{item.collection}</Typography>
                                    </Grid>

                                    <Grid item md={3} xs={6}>
                                        <Card variant="outlined" sx={{ p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}> {t("common.qty")}:</Typography>
                                            <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>
                                                {item.quantity?.toLocaleString()}
                                            </Typography>
                                        </Card>
                                    </Grid>

                                    <Grid item md={3} xs={6}>
                                        <Card variant="outlined" sx={{ p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{t("common.amountIncVat")}:</Typography>
                                            <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>
                                                {item.amountIncludingVAT?.toLocaleString()}
                                            </Typography>
                                        </Card>
                                    </Grid>

                                    <Grid item md={3} xs={6}>
                                        <Card variant="outlined" sx={{ p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                            <Typography variant="body2">{t("common.cbMargin")}:</Typography>
                                            <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>
                                                {item.tbAmount?.toLocaleString()}
                                            </Typography>
                                        </Card>
                                    </Grid>

                                    <Grid item md={3} xs={6}>
                                        <Card variant="outlined" sx={{ p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                        <Typography variant="body2">{t("common.cbPercent")}:</Typography>
                                            <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-word' }} >
                                                {item.tbPercent?.toLocaleString()}%
                                            </Typography>
                                        </Card>
                                    </Grid>

                                </Grid>
                            </Grid>

                        ))}
                        {items.length === 0 && (
                            <Grid item xs={12}>
                                <Typography align="center">{t("common.noItemsFound")}</Typography>
                            </Grid>
                        )}
                    </Grid>
                    </LoadingSection>
                    <ResponsivePagination

                        page={query.page}

                        pageSize={query.pageSize}

                        totalCount={totalCount}

                        onPageChange={handlePageChange}

                    />
                </Paper>
            </CenteredCard>


        </>
    );
};

export default SalesItemReportPage;